import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ContentService } from '../../../../core/services/content.service';
import { SeoService } from '../../../../core/services/seo.service';
import { ContentDetail } from '../../../../core/models';
import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
import { ScrollReveal } from '../../../../shared/directives/scroll-reveal';
import { MediaGalleryCarousel } from '../../../../shared/ui/content/media-gallery-carousel/media-gallery-carousel';

/** Détail d'une actualité par slug (content-service, /contents/public/news/{slug}). */
@Component({
  selector: 'app-news-detail',
  imports: [RouterLink, ScrollReveal, TranslatePipe, LocalizedDatePipe, MediaGalleryCarousel],
  templateUrl: './news-detail.html',
})
export class NewsDetail {
  private readonly route = inject(ActivatedRoute);
  private readonly contentService = inject(ContentService);
  private readonly seo = inject(SeoService);

  readonly loading = signal(true);
  readonly notFound = signal(false);
  readonly article = signal<ContentDetail | null>(null);
  readonly imageUrl = computed(() => this.article()?.coverUrl ?? null);

  constructor() {
    inject(DestroyRef).onDestroy(() => this.seo.removeJsonLd('news-jsonld'));

    const slug = this.route.snapshot.paramMap.get('slug');
    if (!slug) {
      this.notFound.set(true);
      this.loading.set(false);
      return;
    }
    this.contentService.newsArticle(slug).subscribe({
      next: (detail) => {
        this.article.set(detail);
        this.loading.set(false);
        this.seo.set({
          title: detail.seoTitle || detail.title,
          description: detail.seoDescription || detail.summary || `Actualité Youth Connekt Cameroon : ${detail.title}.`,
          image: detail.coverUrl || undefined,
          path: `/news/${detail.slug}`,
          type: 'article',
        });
        this.seo.setJsonLd(
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: detail.title,
            description: detail.summary || undefined,
            image: detail.coverUrl ? [detail.coverUrl] : undefined,
            datePublished: detail.publishedAt || undefined,
            dateModified: detail.publishedAt || undefined,
            author: { '@type': 'Organization', name: 'Youth Connekt Cameroon' },
            publisher: { '@type': 'Organization', name: 'Youth Connekt Cameroon' },
          },
          'news-jsonld',
        );
        // Non bloquant : le tracking analytics ne doit jamais empêcher l'affichage de la fiche.
        this.contentService.trackView(detail.id).subscribe({ error: () => undefined });
      },
      error: () => {
        this.loading.set(false);
        this.notFound.set(true);
      },
    });
  }
}
